import { useMemo, useState } from "react";
import { toast } from "sonner";
import { TreePine, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { SellerAuthGate } from "./SellerAuthGate";
import {
  US_STATES, LAND_USE, PublicShell, Field, SubmissionResult, inputCls,
  RoleAttestation, TimelineField, ReviewProcessNotice, AddressVisibilityNote,
} from "./_shared";

export default function SubmitLand() {
  const [f, setF] = useState({
    parcel_address: "", county: "", state: "", acreage: "", land_use: "", asking_price: "",
    water_access: false, road_access: false, utilities_available: false, zoning: "",
    seller_financing_available: false, seller_financing_terms: "",
    contact_name: "", contact_role: "owner", contact_phone: "",
    listing_agreement_accepted: false,
    owner_attestation: false, representative_attestation: false,
    timeline: "exploring",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const pricePerAcre = useMemo(() => {
    const a = Number(f.acreage), p = Number(f.asking_price);
    return a > 0 && p > 0 ? Math.round(p / a) : null;
  }, [f.acreage, f.asking_price]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.listing_agreement_accepted) { toast.error("Please confirm the listing agreement."); return; }
    setBusy(true);
    try {
      const payload = {
        ...f,
        acreage: Number(f.acreage) || 0,
        asking_price: Number(f.asking_price) || 0,
      };
      const r = await api.post("/listings/submit/land", payload);
      if (r.data?.duplicate) toast.warning("This parcel looks like it is already in our system.");
      else toast.success("Listing received");
      setResult({
        oid: r.data?.oid,
        title: f.parcel_address,
        address: f.parcel_address,
        confirmationEmailSent: r.data?.confirmation_email_sent,
      });
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Submission failed.");
    } finally { setBusy(false); }
  };

  if (result) {
    return (
      <PublicShell seoRoute="/submit-land" title="Submit a Land Listing" testid="submit-land-page">
        <SubmissionResult
          testid="thank-you-land"
          title={result.title}
          address={result.address}
          oid={result.oid}
          confirmationEmailSent={result.confirmationEmailSent}
        />
      </PublicShell>
    );
  }

  return (
    <PublicShell
      seoRoute="/submit-land"
      title="Submit a Land Listing to Acquigine"
      subtitle="For raw land, agricultural, timberland, and rural residential parcels. Submit to a network of acquirers open to owner-financed and creative structures. Every submission is reviewed by our team before it becomes visible to any acquirer, and may be declined."
      testid="submit-land-page"
    >
      <SellerAuthGate>
        <form onSubmit={submit} className="ae-card p-6 space-y-5">
          <div className="flex items-center gap-3 pb-4 border-b border-white/8">
            <div className="h-9 w-9 rounded-sm bg-emerald-500/15 flex items-center justify-center"><TreePine className="h-4 w-4 text-emerald-400" /></div>
            <div className="text-sm font-medium text-slate-100">Parcel details</div>
          </div>

          <Field label="Street address, or parcel number / legal description" required>
            <input className={inputCls} required value={f.parcel_address} onChange={(e) => set("parcel_address", e.target.value)} data-testid="land-address" />
          </Field>
          <AddressVisibilityNote />

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="County" required>
              <input className={inputCls} required value={f.county} onChange={(e) => set("county", e.target.value)} data-testid="land-county" />
            </Field>
            <Field label="State" required>
              <select className={inputCls} required value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="land-state">
                <option value="">Select state</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Acreage" required>
              <input className={inputCls} type="number" min="0" step="0.01" required value={f.acreage} onChange={(e) => set("acreage", e.target.value)} data-testid="land-acreage" />
            </Field>
            <Field label="Asking price ($)" required>
              <input className={inputCls} type="number" min="0" required value={f.asking_price} onChange={(e) => set("asking_price", e.target.value)} data-testid="land-asking" />
            </Field>
            <Field label="Price per acre" hint="Auto-calculated">
              <input className={inputCls} disabled value={pricePerAcre ? `$${pricePerAcre.toLocaleString()}` : ""} data-testid="land-price-per-acre" />
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Land use type">
              <select className={inputCls} value={f.land_use} onChange={(e) => set("land_use", e.target.value)} data-testid="land-use">
                <option value="">Select use</option>
                {LAND_USE.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
            <Field label="Zoning">
              <input className={inputCls} value={f.zoning} onChange={(e) => set("zoning", e.target.value)} data-testid="land-zoning" />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Toggle k="water_access" label="Water access" f={f} set={set} testid="land-water" />
            <Toggle k="road_access" label="Road access" f={f} set={set} testid="land-road" />
            <Toggle k="utilities_available" label="Utilities" f={f} set={set} testid="land-util" />
          </div>

          <div className="pt-3 border-t border-white/8">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={f.seller_financing_available} onChange={(e) => set("seller_financing_available", e.target.checked)} data-testid="land-sf-yes" />
              <span className="text-sm text-slate-200">Seller financing available</span>
            </label>
            {f.seller_financing_available && (
              <Field label="Terms (if known)">
                <textarea className={`${inputCls} mt-3`} rows={2} value={f.seller_financing_terms} onChange={(e) => set("seller_financing_terms", e.target.value)} data-testid="land-sf-terms" />
              </Field>
            )}
          </div>

          <div className="pt-3 border-t border-white/8 grid md:grid-cols-2 gap-4">
            <Field label="Your name" required>
              <input className={inputCls} required value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="land-contact-name" />
            </Field>
            <Field label="Phone">
              <input className={inputCls} value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="land-contact-phone" />
            </Field>
          </div>

          <RoleAttestation
            role={f.contact_role}
            setRole={(v) => set("contact_role", v)}
            ownerAck={f.owner_attestation}
            setOwnerAck={(v) => set("owner_attestation", v)}
            repAck={f.representative_attestation}
            setRepAck={(v) => set("representative_attestation", v)}
            prefix="land"
          />

          <div className="pt-2">
            <TimelineField value={f.timeline} onChange={(v) => set("timeline", v)} prefix="land" />
          </div>

          <ReviewProcessNotice />

          <label className="flex items-start gap-3 p-3 rounded-sm border border-white/10 cursor-pointer">
            <input type="checkbox" required checked={f.listing_agreement_accepted} onChange={(e) => set("listing_agreement_accepted", e.target.checked)} className="mt-1" data-testid="land-agreement" />
            <span className="text-xs text-slate-300">I confirm I have the right to list this parcel and that the information provided is accurate.</span>
          </label>

          <button type="submit" disabled={busy} data-testid="land-submit" className="btn-primary w-full justify-center inline-flex items-center gap-2 disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? "Submitting..." : "Submit listing"}
          </button>
        </form>
      </SellerAuthGate>
    </PublicShell>
  );
}

function Toggle({ k, label, f, set, testid }) {
  return (
    <label className="flex items-center gap-2 p-3 rounded-sm border border-white/8 cursor-pointer hover:border-emerald-500/30 transition">
      <input type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)} data-testid={testid} />
      <span className="text-xs text-slate-200">{label}</span>
    </label>
  );
}
